/**
 * Where a signed-in session lives in the browser.
 *
 * `apps/web/src/api/client.ts` holds the ACCESS token in a module variable and hands it
 * to every call. This file is the other half: which token is written down, where, and
 * what is done to make sure it is never spent twice.
 *
 * ── THE REFRESH TOKEN IS THE SENSITIVE ONE, AND ROTATION IS WHY ──────────────
 *
 * `apps/api/src/auth/device-trust.ts § rotate` mints a new refresh token on every use
 * and remembers the old one as consumed for thirty days. If a consumed token comes
 * back, the server cannot tell the user from a thief, so it revokes the WHOLE device
 * chain and the person has to verify by OTP again. That is the right call on the
 * server. It means a client that can submit the same refresh token twice has built
 * itself a lockout, and during a WhatsApp outage — the exact case device trust exists
 * for — that lockout has no way out (docs/07 § 1).
 *
 * Two things follow, and they pull against each other:
 *
 *   · The token must SURVIVE a reload and a browser restart. A token held only in
 *     memory is gone the moment the tab closes, which throws away the ninety days of
 *     device trust and sends the user back to the OTP channel — the one thing
 *     docs/07 § 1 says a ban must not be able to force.
 *   · It must be spent by ONE caller at a time. Two tabs waking up together, or a
 *     duplicated tab carrying a copy of its `sessionStorage`, are enough to submit the
 *     same token twice.
 *
 * So: ONE durable copy in `localStorage` — one key, shared by every tab of the origin,
 * so there is never a second copy to go stale — and every read-POST-write cycle runs
 * inside a mutual exclusion that spans tabs (`navigator.locks`) as well as an
 * in-process single-flight promise. The rotated token is written back before the lock
 * is released, so the next caller reads the new one and never the spent one.
 *
 * ── WHAT THIS CHOICE COSTS, STATED PLAINLY ───────────────────────────────────
 *
 * `localStorage` is readable by script, so any XSS on the origin reads the refresh
 * token. The better home is a `HttpOnly; Secure; SameSite=Lax` cookie set by the
 * Worker on `/v1/auth/*`, which script cannot read at all and which no client bug can
 * double-submit. That is an API change — `/session/refresh` takes the token in the
 * request body today — and `apps/api/**` is not this change's to make. REPORTED as the
 * next move rather than approximated here: `sessionStorage` would not fix the exposure
 * (script reads it just the same) and would break the survival requirement above, so
 * it is strictly worse on both counts.
 *
 * ── AND WHY A FAILED REFRESH IS NEVER RETRIED ────────────────────────────────
 *
 * If the POST is sent and the answer is lost, the server has already rotated: our
 * token is consumed and re-sending it is precisely what revokes the device. So a
 * network failure during refresh resolves to `'unreachable'` and stops. One lost
 * answer must not become a revoked device chain through a retry loop.
 */
import { ApiError, NetworkError, api, bindSessionRenewal, setAccessToken } from '../api/client.js';

/** One durable copy, one key. See the header for why there is exactly one. */
const REFRESH_KEY = 'oprappo.auth.refresh.v1';
/** The device this browser calls itself, so trust and revocation have a stable target. */
const DEVICE_KEY = 'oprappo.auth.device.v1';
/** The name the cross-tab lock is taken under. */
const REFRESH_LOCK = 'oprappo.auth.refresh';
/** Set once this device has been sent to the start of onboarding. See `firstRunDestination`. */
const ONBOARDING_SEEN_KEY = 'oprappo.onboarding.seen.v1';
/** The signed-in person's own name, as the server last sent it. See `cachedDisplayName`. */
const DISPLAY_NAME_KEY = 'oprappo.profile.name.v1';

/**
 * `localStorage` is not always there and not always usable — Safari's private mode
 * throws on write, and a `node` test environment has no such global at all. A session
 * that cannot be written down still has to work for the life of the tab, so the
 * fallback is a module-level map rather than a thrown error.
 */
const memory = new Map<string, string>();

function readStore(key: string): string | null {
  try {
    const value = globalThis.localStorage?.getItem(key);
    if (value !== null && value !== undefined) return value;
  } catch {
    /* unusable storage is the same as empty storage */
  }
  return memory.get(key) ?? null;
}

function writeStore(key: string, value: string): void {
  memory.set(key, value);
  try {
    globalThis.localStorage?.setItem(key, value);
  } catch {
    /* the in-memory copy above is what keeps this tab working */
  }
}

function clearStore(key: string): void {
  memory.delete(key);
  try {
    globalThis.localStorage?.removeItem(key);
  } catch {
    /* nothing to do: the value is already gone from the only other copy */
  }
}

/**
 * This browser's device id, minted once and kept.
 *
 * The Worker's `deviceOf()` invents a fresh UUID when the client sends none
 * (`apps/api/src/auth/index.ts`), which would give the same person a new "device" on
 * every sign-in: Account § sessions would fill with strangers and revoking one of them
 * would revoke nothing the user recognises. Sending a stable id is what makes that
 * list mean something.
 *
 * `device_label` is deliberately NOT sent. The label is what a person reads in Account
 * § sessions, the design draws no control for naming a device, and the Worker already
 * has a reviewed Bangla default for it. Inventing one here is the one thing docs/01
 * forbids.
 */
export function deviceId(): string {
  const held = readStore(DEVICE_KEY);
  if (held !== null && held !== '') return held;
  const minted = crypto.randomUUID();
  writeStore(DEVICE_KEY, minted);
  return minted;
}

/** The tokens every successful auth call hands back. */
export interface SessionTokens {
  access_token: string;
  access_expires_at: string;
  refresh_token: string;
  refresh_expires_at: string;
}

/** True when this browser holds a refresh token it can try to renew with. */
export function hasStoredSession(): boolean {
  return readStore(REFRESH_KEY) !== null;
}

/** Exposed for the tests and for `refreshSession`; not part of the flow's vocabulary. */
export function storedRefreshToken(): string | null {
  return readStore(REFRESH_KEY);
}

/**
 * Take a freshly issued session: the access token goes to the transport, the refresh
 * token to the one durable slot.
 */
export function adoptSession(tokens: SessionTokens): void {
  writeStore(REFRESH_KEY, tokens.refresh_token);
  setAccessToken(tokens.access_token);
}

/** Drop everything this browser holds. Called on any refusal, and on sign-out. */
export function forgetSession(): void {
  clearStore(REFRESH_KEY);
  clearStore(DISPLAY_NAME_KEY);
  setAccessToken(null);
}

export type RefreshOutcome =
  /** A live session is now in hand. */
  | 'restored'
  /** Nothing was stored; this browser has never signed in, or has signed out. */
  | 'no_token'
  /** The server refused. The stored token is gone; the door is the OTP flow. */
  | 'rejected'
  /** The request did not complete. The token is untouched and is NOT re-sent. */
  | 'unreachable';

/** The in-process half of the mutual exclusion. See `withRefreshLock`. */
let inFlight: Promise<RefreshOutcome> | null = null;

/**
 * Run `fn` with nothing else refreshing, in this tab or any other.
 *
 * `navigator.locks` is the only cross-tab mutual exclusion a browser offers, and it is
 * absent in a non-secure context and in the test environment. Its absence degrades to
 * the in-process guard alone, which still covers the common double-submit — two
 * components mounting at once — and leaves only the genuinely multi-tab case to the
 * server's re-use detection.
 */
async function withRefreshLock(fn: () => Promise<RefreshOutcome>): Promise<RefreshOutcome> {
  const locks = globalThis.navigator?.locks;
  if (locks === undefined) return fn();
  return locks.request(REFRESH_LOCK, fn);
}

/**
 * Renew this device's session — the path a channel outage must never touch.
 *
 * docs/07 § 1: a trusted device renews itself with no OTP and no WhatsApp. There is no
 * retry here for the reason in the header, and the stored token is replaced with the
 * rotated one before the lock is released.
 */
export function refreshSession(): Promise<RefreshOutcome> {
  if (inFlight !== null) return inFlight;
  const run = withRefreshLock(async (): Promise<RefreshOutcome> => {
    // Read INSIDE the lock: another tab may have rotated it while this call waited.
    const token = readStore(REFRESH_KEY);
    if (token === null || token === '') return 'no_token';
    try {
      const tokens = await api.post<SessionTokens>(
        '/v1/auth/session/refresh',
        { refresh_token: token },
        { anonymous: true },
      );
      adoptSession(tokens);
      return 'restored';
    } catch (cause) {
      if (cause instanceof NetworkError) return 'unreachable';
      // Anything the server answered — a rejected rotation is a 401 with an `error`
      // envelope, an expired one the same — means this token will never work again.
      // Keeping it would only offer it to a later call, which is how a stale token
      // gets replayed into the re-use detector.
      forgetSession();
      return 'rejected';
    }
  }).finally(() => {
    inFlight = null;
  });
  inFlight = run;
  return run;
}

/**
 * THE TRANSPORT RENEWS THROUGH THIS FILE, AND ONLY THROUGH THIS FILE.
 *
 * `api/client.ts` meets the expired access token first — as a 401 on some GET — and it
 * must not spend the refresh token itself: the lock, the read-inside-the-lock and the
 * no-retry rule above are the only safe way to do that, and a second implementation in the
 * transport would be a second chance to get rotation wrong. So it is handed this one.
 *
 * Handed, not imported: this module imports the transport, and the transport importing
 * it back would be a cycle between the two files that hold the credentials. Bound when
 * this module loads, which `main.tsx` guarantees happens before the first render.
 *
 * A renewal the transport asks for is the "different caller with a different reason"
 * `restoreSession` below describes: a person pressing retry on a screen after the network
 * came back, not a loop. Each 401 burst gets one renewal (`client.ts § renewOnce`), and a
 * request is never renewed for twice.
 */
bindSessionRenewal(refreshSession);

/**
 * True when the failure is the server telling us this identity may not act yet.
 *
 * Kept next to the session because it is a fact about the session, not about a screen:
 * `E_VERIFICATION_PENDING` is what a deferred identity is refused with (docs/07 § 4).
 */
export function isVerificationPending(cause: unknown): cause is ApiError {
  return cause instanceof ApiError && cause.code === 'E_VERIFICATION_PENDING';
}

/* ═══════════════════════════════════════════════════════ boot restore ══ */

/**
 * THE RELOAD THAT SIGNED EVERYBODY OUT.
 *
 * Everything above was in place and nothing called it. `adoptSession` wrote the refresh
 * token to `localStorage` and handed the access token to a MODULE VARIABLE in
 * `api/client.ts` — and a module variable does not survive a reload. So the durable half
 * of the session was durable and the spendable half was not: a returning customer had a
 * valid ninety-day device trust sitting in storage, an empty `Authorization` header on
 * every call, and no code anywhere that spent the one to get the other. Pressing reload
 * was a sign-out. Closing the tab was a sign-out. The whole of docs/07 § 1 — "a trusted
 * device renews itself with no OTP and no WhatsApp" — was written, tested and never run.
 *
 * This is the missing call, and it is deliberately NOT a new refresh path:
 * `refreshSession()` above already holds the cross-tab lock, the in-process
 * single-flight, the read-inside-the-lock and the rule that a network failure is never
 * retried. A second implementation here would be a second chance to get rotation wrong.
 *
 * ── WHY IT IS ONCE, AND ONLY ONCE ────────────────────────────────────────────
 * `refreshSession()` clears its single-flight when it settles, so calling it twice after
 * an `'unreachable'` would re-send a token the server may already have rotated — which
 * is exactly the double-submit that revokes the device chain and sends a person back to
 * the OTP channel during the outage device trust exists to survive. The boot is one
 * event, so it gets one promise: every later caller gets the SAME promise back, not a
 * second attempt. A deliberate re-auth after a 401 is a different caller with a
 * different reason and still calls `refreshSession()` directly.
 */
let bootAttempt: Promise<RefreshOutcome> | null = null;

/**
 * Spend the stored refresh token once, at startup.
 *
 * COSTS NOTHING WHEN THERE IS NOTHING TO RESTORE. With no stored token this resolves
 * `'no_token'` without a fetch, without taking the lock and without a timer — which is
 * the case the visual gate, every test environment and every first-time visitor are in,
 * and it is why mounting can wait on this without the gate waiting on anything.
 */
export function restoreSession(): Promise<RefreshOutcome> {
  if (bootAttempt !== null) return bootAttempt;
  const attempt: Promise<RefreshOutcome> = hasStoredSession()
    ? refreshSession()
    : Promise.resolve<RefreshOutcome>('no_token');
  bootAttempt = attempt;
  return attempt;
}

/** True once `restoreSession` has been called. Exposed for the tests, not for the flow. */
export function sessionRestoreStarted(): boolean {
  return bootAttempt !== null;
}

/**
 * Mount the app, after the session is back if there is one to bring back.
 *
 * THE ORDER IS THE POINT, AND SO IS THE BRANCH. A returning customer's first render has
 * to happen with the access token already in `api/client.ts`, or every authenticated
 * read on that first screen races the restore and loses — the customer sees অনুমতি নেই
 * on their own account page and then watches it correct itself, which reads as a bug
 * even when it heals. A visitor with NO stored token must not pay for that: there is
 * nothing to wait for, so `mount` is called synchronously, in this tick, and the first
 * paint is byte for byte what it was before this function existed. That branch is what
 * keeps the 2% gate measuring the same thing — the gate's browser has empty storage.
 *
 * `.finally` and not `.then`: a refusal and an unreachable server both still mount. The
 * outcome decides whether there is a session, never whether there is an app.
 */
export function mountWhenSessionRestored(mount: () => void): void {
  if (!hasStoredSession()) {
    void restoreSession();
    mount();
    return;
  }
  void restoreSession().finally(mount);
}

/* ════════════════════════════════════════════════════ first run ══ */

/**
 * WHERE A FIRST VISIT BEGINS.
 *
 * A1 · ভাষা নির্বাচন is the first screen of the product (`Oprappo P0 Screens.dc.html`, flow
 * A), and nothing ever sent anybody to it: `/` is home, so a person opening oprappo.com for
 * the first time landed on a home screen built for somebody who already has an account,
 * and met the sign-in question only when a screen refused them.
 *
 * FIRST RUN means both: no refresh token on this device (nobody has signed in here) AND
 * no record that this device was already sent to the start. The second half is what makes
 * it once — a visitor who reads A1, goes back and opens `/` again is choosing home, and
 * must get it. The record is written by `markOnboardingSeen` at the moment of the send,
 * so a person who never finishes onboarding is not sent back to it every visit.
 *
 * ONLY FROM `/`, and only when nothing else was asked for: a shared link to an offering,
 * a search, a receipt, or a `?state=` capture is an address somebody chose, and it opens.
 *
 * NEVER ON THE FOUNDER HOST. `founder.oprappo.com` shares this build and its `/` is the
 * founder door, not a customer's first screen (founder decision 2026-09-18, Option A).
 * Read from the hostname, the same test W1-03's `host.ts` makes; when both land, this
 * line should become `appHost() === 'founder'`.
 *
 * NEVER IN A HARNESS BUILD. The visual gate loads `/` in a browser with empty storage —
 * which is a first run by this definition — and measures B1 against its frame. `harness`
 * is `states/boundary.tsx § stateSeamOpen()`, the same one-line rule that decides whether
 * `?state=` is reachable: a build that cannot reach the real API is a build that exists
 * to be measured.
 *
 * Storage is read through `readStore`, so an unusable `localStorage` (a private window,
 * a blocked origin) reads as empty rather than throwing — and a first run there sends the
 * person to A1 once per tab, which is the honest reading of a device that forgets.
 */
export const FIRST_RUN_PATH = '/welcome/language';

export function isFirstRun(): boolean {
  return !hasStoredSession() && readStore(ONBOARDING_SEEN_KEY) === null;
}

export function markOnboardingSeen(): void {
  writeStore(ONBOARDING_SEEN_KEY, '1');
}

export function firstRunDestination(where: {
  hostname: string;
  pathname: string;
  search: string;
  harness: boolean;
}): string | null {
  if (where.harness) return null;
  if (where.hostname.startsWith('founder.')) return null;
  if (where.pathname !== '/' || where.search !== '') return null;
  return isFirstRun() ? FIRST_RUN_PATH : null;
}

/* ═════════════════════════════════════════════════════ whose name ══ */

/**
 * The signed-in person's own name, as the server last sent it — or `null`.
 *
 * ইন্টারনেট নেই (`P0 States § ৬`) greets the person by name over a screen that cannot
 * reach the server, which is exactly when the name cannot be fetched. It used to greet
 * everybody as `রফিকুল ইসলাম`, the person the frame was drawn for. Now it greets them with
 * what this device last heard from `GET /v1/account/profile`, or with the greeting alone.
 *
 * Kept beside the session because it belongs to it: `forgetSession` clears it with the
 * refresh token, so a shared phone never greets the next person by the last one's name.
 * Written by whichever screen reads the profile (`rememberDisplayName`); nothing here
 * composes, abbreviates or guesses one.
 */
export function cachedDisplayName(): string | null {
  if (!hasStoredSession()) return null;
  const name = readStore(DISPLAY_NAME_KEY);
  return name === null || name.trim() === '' ? null : name;
}

export function rememberDisplayName(name: string | null): void {
  if (name === null || name.trim() === '') clearStore(DISPLAY_NAME_KEY);
  else writeStore(DISPLAY_NAME_KEY, name);
}
